import { useCallback, useEffect, useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { useSession } from '../lib/session';
import { getStripe, stripeConfigured } from '../lib/stripe';
import { fetchMyRequiresActionCharge, fetchAuctionChargeSecret } from '../lib/queries';
import type { AuctionCharge } from '../lib/types';
import './AddCardModal.css';
import './CompleteWinningPayment.css';

/**
 * Global banner for a member whose winning-auction charge failed off-session
 * SCA (status requires_action). Prompts them to complete the bank security
 * check on-session; on success the webhook flips the ledger to succeeded.
 * Renders nothing when there's no charge to action.
 */

export function CompleteWinningPayment() {
  const { profile } = useSession();
  const [charge, setCharge] = useState<AuctionCharge | null>(null);
  const [open, setOpen] = useState(false);

  const reload = useCallback(() => {
    if (!profile) { setCharge(null); return; }
    fetchMyRequiresActionCharge()
      .then(setCharge)
      .catch(() => { /* non-fatal — just don't show the banner */ });
  }, [profile?.id]);

  useEffect(() => { reload(); }, [reload]);

  if (!profile || !stripeConfigured || !charge) return null;

  return (
    <>
      <div className="winpay" role="status">
        <span className="winpay__text">
          You won an auction. Complete your payment of {dollars(charge.amount_cents)} to secure your lot.
        </span>
        <button type="button" className="winpay__btn" onClick={() => setOpen(true)}>
          Complete payment
        </button>
      </div>
      {open && (
        <CompleteModal
          charge={charge}
          onClose={() => setOpen(false)}
          onPaid={() => { setOpen(false); reload(); }}
        />
      )}
    </>
  );
}

function CompleteModal({ charge, onClose, onPaid }: { charge: AuctionCharge; onClose: () => void; onPaid: () => void }) {
  // Lazy: Stripe.js loads only when this modal opens, not app-wide.
  const stripePromise = useMemo(() => getStripe(), []);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  useEffect(() => {
    let cancelled = false;
    fetchAuctionChargeSecret(charge.id)
      .then((cs) => { if (!cancelled) setClientSecret(cs); })
      .catch((err) => { if (!cancelled) setLoadError(err?.message ?? 'Could not load the payment.'); });
    return () => { cancelled = true; };
  }, [charge.id]);

  const onBackdrop = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const options: StripeElementsOptions | undefined = clientSecret
    ? { clientSecret, appearance: { theme: 'flat', variables: { borderRadius: '10px' } } }
    : undefined;

  return (
    <div className="addcard" role="dialog" aria-modal="true" aria-label="Complete winning payment" onClick={onBackdrop}>
      <div className="addcard__panel">
        <button type="button" className="addcard__close" onClick={onClose} aria-label="Close">×</button>
        <p className="kicker addcard__kicker">Sales Room</p>
        <h2 className="addcard__title">Complete your payment</h2>
        <p className="addcard__lede">
          Your bank asked for an extra security check before this {dollars(charge.amount_cents)} payment
          could go through. Confirm it below to secure your lot.
        </p>
        {loadError && <p className="addcard__error" role="alert">{loadError}</p>}
        {!loadError && !clientSecret && <p className="addcard__loading">Loading secure payment…</p>}
        {options && (
          <Elements stripe={stripePromise} options={options}>
            <PayForm onClose={onClose} onPaid={onPaid} />
          </Elements>
        )}
      </div>
    </div>
  );
}

function PayForm({ onClose, onPaid }: { onClose: () => void; onPaid: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || busy) return;
    setBusy(true);
    setError(null);
    const { error: payErr } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.href },
    });
    if (payErr) {
      setError(payErr.message ?? 'Payment could not be completed. Try again.');
      setBusy(false);
      return;
    }
    onPaid();
  }

  return (
    <form className="addcard__form" onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && <p className="addcard__error" role="alert">{error}</p>}
      <div className="addcard__actions">
        <button type="button" className="btn btn--ghost btn--md" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="submit" className="btn btn--primary btn--md" disabled={!stripe || busy}>
          {busy ? 'Confirming…' : 'Pay now'}
        </button>
      </div>
    </form>
  );
}

function dollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}
